/**
 * Shopping Cart Context - E-commerce Style Booking Cart
 * Manages cart state, localStorage sync, and API calls
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

const CART_SESSION_KEY = 'easystay_cart_session_id';

// Create Context
const CartContext = createContext();

// Cart Provider Component
export function CartProvider({ children }) {
  const [cart, setCart] = useState({
    items: [],
    pricing: {
      subtotal: 0,
      discount_percent: 0,
      discount_amount: 0,
      total: 0,
      savings: 0
    },
    item_count: 0,
    session_id: null,
    loading: false
  });

  // Load cart from backend on mount
  useEffect(() => {
    loadCart();
  }, []);

  // Save session_id to localStorage whenever it changes
  useEffect(() => {
    if (cart.session_id) {
      localStorage.setItem(CART_SESSION_KEY, cart.session_id);
    }
  }, [cart.session_id]);

  /**
   * Load cart from backend
   */
  const loadCart = async () => {
    try {
      const sessionId = localStorage.getItem(CART_SESSION_KEY);
      
      if (!sessionId) {
        return; // No session, cart is empty
      }

      const response = await fetch(`${BACKEND_URL}/api/cart/${sessionId}`);
      const data = await response.json();

      if (data.success && data.cart) {
        setCart({
          items: data.cart.items || [],
          pricing: data.cart.pricing || {},
          item_count: data.cart.item_count || 0,
          session_id: sessionId,
          loading: false
        });
      } else {
        // Cart expired or not found, clear localStorage
        localStorage.removeItem(CART_SESSION_KEY);
        setCart({
          items: [],
          pricing: { subtotal: 0, total: 0 },
          item_count: 0,
          session_id: null,
          loading: false
        });
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
    }
  };

  /**
   * Add item to cart
   * @param {string} itemType - 'guest_room' or 'meeting_room'
   * @param {object} itemData - Item details
   */
  const addToCart = async (itemType, itemData) => {
    try {
      setCart(prev => ({ ...prev, loading: true }));

      const response = await fetch(`${BACKEND_URL}/api/cart/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: cart.session_id,
          item_type: itemType,
          item_data: itemData
        })
      });

      const data = await response.json();

      if (data.success) {
        setCart({
          items: data.cart.items,
          pricing: data.cart.pricing,
          item_count: data.cart.item_count,
          session_id: data.session_id,
          loading: false
        });

        const itemName = itemType === 'guest_room' 
          ? `${itemData.hotel_name} - ${itemData.room_type}`
          : `${itemData.room_name} at ${itemData.hotel_name}`;

        toast.success(`Added to cart!`, {
          description: itemName,
          action: {
            label: 'View Cart',
            onClick: () => {
              // Dispatch custom event to open cart drawer
              window.dispatchEvent(new CustomEvent('openCartDrawer'));
            }
          }
        });

        return { success: true, item_id: data.item_id };
      } else {
        throw new Error(data.error || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      toast.error('Failed to add to cart', {
        description: error.message
      });
      setCart(prev => ({ ...prev, loading: false }));
      return { success: false, error: error.message };
    }
  };

  /**
   * Remove item from cart
   * @param {string} itemId - Item ID to remove
   */
  const removeFromCart = async (itemId) => {
    try {
      if (!cart.session_id) return;

      setCart(prev => ({ ...prev, loading: true }));

      const response = await fetch(
        `${BACKEND_URL}/api/cart/${cart.session_id}/item/${itemId}`,
        { method: 'DELETE' }
      );

      const data = await response.json();

      if (data.success) {
        setCart({
          items: data.cart.items,
          pricing: data.cart.pricing,
          item_count: data.cart.item_count,
          session_id: cart.session_id,
          loading: false
        });

        toast.success('Item removed from cart');
        return { success: true };
      } else {
        throw new Error(data.error || 'Failed to remove item');
      }
    } catch (error) {
      console.error('Remove from cart error:', error);
      toast.error('Failed to remove item');
      setCart(prev => ({ ...prev, loading: false }));
      return { success: false, error: error.message };
    }
  };

  /**
   * Update cart item
   * @param {string} itemId - Item ID to update
   * @param {object} itemData - Updated item data
   */
  const updateCartItem = async (itemId, itemData) => {
    try {
      if (!cart.session_id) return;

      setCart(prev => ({ ...prev, loading: true }));

      const response = await fetch(
        `${BACKEND_URL}/api/cart/${cart.session_id}/item/${itemId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_data: itemData })
        }
      );

      const data = await response.json();

      if (data.success) {
        setCart({
          items: data.cart.items,
          pricing: data.cart.pricing,
          item_count: data.cart.item_count,
          session_id: cart.session_id,
          loading: false
        });

        toast.success('Cart updated');
        return { success: true };
      } else {
        throw new Error(data.error || 'Failed to update item');
      }
    } catch (error) {
      console.error('Update cart error:', error);
      toast.error('Failed to update cart');
      setCart(prev => ({ ...prev, loading: false }));
      return { success: false, error: error.message };
    }
  };

  /**
   * Clear entire cart
   */
  const clearCart = async () => {
    try {
      if (!cart.session_id) {
        // Already empty
        return { success: true };
      }

      const response = await fetch(
        `${BACKEND_URL}/api/cart/${cart.session_id}`,
        { method: 'DELETE' }
      );

      const data = await response.json();

      if (data.success) {
        localStorage.removeItem(CART_SESSION_KEY);
        setCart({
          items: [],
          pricing: { subtotal: 0, total: 0 },
          item_count: 0,
          session_id: null,
          loading: false
        });

        toast.success('Cart cleared');
        return { success: true };
      }
    } catch (error) {
      console.error('Clear cart error:', error);
      toast.error('Failed to clear cart');
      return { success: false, error: error.message };
    }
  };

  /**
   * Get item by ID
   * @param {string} itemId - Item ID
   */
  const getCartItem = (itemId) => {
    return cart.items.find(item => item.id === itemId);
  };

  /**
   * Check if item exists in cart
   * @param {string} itemType - 'guest_room' or 'meeting_room'
   * @param {object} checkData - Data to match (hotel_id, dates, etc.)
   */
  const isInCart = (itemType, checkData) => {
    return cart.items.some(item => {
      if (item.type !== itemType) return false;

      if (itemType === 'guest_room') {
        return (
          item.hotel_id === checkData.hotel_id &&
          item.check_in === checkData.check_in &&
          item.check_out === checkData.check_out &&
          item.room_type === checkData.room_type
        );
      } else if (itemType === 'meeting_room') {
        return (
          item.room_id === checkData.room_id &&
          item.booking_date === checkData.booking_date &&
          item.start_time === checkData.start_time &&
          item.end_time === checkData.end_time
        );
      }

      return false;
    });
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    loadCart,
    getCartItem,
    isInCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

// Custom hook to use cart
export function useCart() {
  const context = useContext(CartContext);
  
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  
  return context;
}
